import { create } from "@bufbuild/protobuf";
import { EquipmentKind, SlotKind, WeaponLoadoutSchema } from "../../../api/types";
import type {
  AttachmentId,
  AttachmentInfo,
  CamoId,
  EquipmentId,
  FullLoadout,
  PerkId,
  WeaponId,
  WeaponLoadout,
} from "../../../api/types";

export const MAX_ATTACHMENTS = 3;
export const PERK_COUNT = 2;
/** Loadout name length in characters (code points), as validated by the server (`gr_core::loadout`). */
export const NAME_MIN = 1;
export const NAME_MAX = 24;
export const NO_CAMO: CamoId = "none";

/** The two loadout slots that hold a weapon (melee is not customizable). */
export type WeaponSlot = SlotKind.PRIMARY | SlotKind.SECONDARY;

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((x) => b.includes(x));
}

function sameWeapon(a: WeaponLoadout, b: WeaponLoadout): boolean {
  return a.weapon === b.weapon && a.camo === b.camo && sameSet(a.attachments, b.attachments);
}

/** Order of attachments and perks does not matter, so toggling an item off and on is not a change. */
export function loadoutsEqual(a: FullLoadout, b: FullLoadout): boolean {
  return (
    a.name === b.name &&
    a.lethal === b.lethal &&
    a.tactical === b.tactical &&
    sameSet(a.perks, b.perks) &&
    sameWeapon(a.primary, b.primary) &&
    sameWeapon(a.secondary, b.secondary)
  );
}

function weaponAt(l: FullLoadout, slot: WeaponSlot): WeaponLoadout {
  return slot === SlotKind.PRIMARY ? l.primary : l.secondary;
}

function withWeapon(l: FullLoadout, slot: WeaponSlot, w: WeaponLoadout): FullLoadout {
  return slot === SlotKind.PRIMARY ? { ...l, primary: w } : { ...l, secondary: w };
}

/** A new weapon starts without attachments and camo: the old weapon's attachments are invalid on it. */
export function chooseWeapon(l: FullLoadout, slot: WeaponSlot, weapon: WeaponId): FullLoadout {
  if (weaponAt(l, slot).weapon === weapon) return l;
  return withWeapon(l, slot, create(WeaponLoadoutSchema, { weapon, camo: NO_CAMO }));
}

/**
 * Toggle an attachment: choosing the selected one clears it, choosing another one of the same slot
 * replaces the old one, and a new slot is refused when MAX_ATTACHMENTS are already chosen.
 */
export function toggleAttachment(
  l: FullLoadout,
  slot: WeaponSlot,
  id: AttachmentId,
  catalog: ReadonlyMap<AttachmentId, AttachmentInfo>,
): FullLoadout {
  const w = weaponAt(l, slot);
  if (w.attachments.includes(id)) {
    return withWeapon(l, slot, { ...w, attachments: w.attachments.filter((a) => a !== id) });
  }
  const target = catalog.get(id)?.slot;
  const kept = w.attachments.filter((a) => catalog.get(a)?.slot !== target);
  if (kept.length >= MAX_ATTACHMENTS) return l;
  return withWeapon(l, slot, { ...w, attachments: [...kept, id] });
}

export function chooseCamo(l: FullLoadout, slot: WeaponSlot, camo: CamoId): FullLoadout {
  return withWeapon(l, slot, { ...weaponAt(l, slot), camo });
}

export function chooseEquipment(l: FullLoadout, kind: EquipmentKind, id: EquipmentId): FullLoadout {
  return kind === EquipmentKind.LETHAL ? { ...l, lethal: id } : { ...l, tactical: id };
}

/** Toggle a perk; a new one is refused when PERK_COUNT are already chosen. */
export function togglePerk(l: FullLoadout, id: PerkId): FullLoadout {
  if (l.perks.includes(id)) return { ...l, perks: l.perks.filter((p) => p !== id) };
  if (l.perks.length >= PERK_COUNT) return l;
  return { ...l, perks: [...l.perks, id] };
}
